/**
 * 游戏模块 API 服务
 * 需要登录认证
 */

import type {
  SecurityFields,
  ListGamesRequest,
  GameInfo,
  LaunchGameRequest,
  LaunchGameResponse,
  RelaunchGameRequest,
  ExitGameRequest,
  HeartbeatRequest,
  PaginatedResponse,
  ListUserGamesData,
  ListBetRecordsRequest,
  ListBetRecordsData,
} from '@/types/api'
import { post } from '@/services/request'

/**
 * 获取游戏列表
 */
export async function getGameList(params: Omit<ListGamesRequest, keyof SecurityFields> = {}): Promise<{
  success: boolean
  data?: PaginatedResponse<GameInfo>
  error?: string
}> {
  try {
    const response = await post<typeof params, PaginatedResponse<GameInfo>>('/api/v1/game/list', params, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取游戏列表失败' }
  } catch (error) {
    console.error('Get game list error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 启动游戏
 */
export async function launchGame(params: Omit<LaunchGameRequest, keyof SecurityFields>): Promise<{
  success: boolean
  data?: LaunchGameResponse
  error?: string
  /** 后端业务错误码 (如 200002 已有游戏会话), 见 docs/25-live-frontend-integration.md 错误码表 */
  code?: number
}> {
  try {
    const response = await post<typeof params, LaunchGameResponse>('/api/v1/game/launch', params, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '启动游戏失败', code: response.code }
  } catch (error) {
    console.error('Launch game error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 重新进入游戏 (目前只有预测市场 provider_code='pm' 支持)
 *
 * 嵌入页登录态只存在页面内存里, iframe/页面一刷新就失效, 而 launch_url 又是
 * 一次性的不能重新打开; 这个接口对同一个 session_id 换一个新的 launch_url,
 * 不会重新带入资金 (区别于 launchGame, 48-pm-frontend-integration.md 第四节)。
 */
export async function relaunchGame(params: Omit<RelaunchGameRequest, keyof SecurityFields>): Promise<{
  success: boolean
  data?: LaunchGameResponse
  error?: string
  /** 后端业务错误码 (如 200006 会话已结束/200014 过于频繁), 见 48-pm-frontend-integration.md 错误码表 */
  code?: number
}> {
  try {
    const response = await post<typeof params, LaunchGameResponse>('/api/v1/game/relaunch', params, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '重新进入游戏失败', code: response.code }
  } catch (error) {
    console.error('Relaunch game error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 退出游戏
 */
export async function exitGame(sessionId: string, options: { keepalive?: boolean } = {}): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<Omit<ExitGameRequest, keyof SecurityFields>, object>(
      '/api/v1/game/exit',
      { session_id: sessionId },
      { auth: true, keepalive: options.keepalive }
    )

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '退出游戏失败' }
  } catch (error) {
    console.error('Exit game error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 游戏心跳保活
 */
export async function gameHeartbeat(sessionId: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<Omit<HeartbeatRequest, keyof SecurityFields>, object>(
      '/api/v1/game/heartbeat',
      { session_id: sessionId },
      { auth: true }
    )

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '心跳失败' }
  } catch (error) {
    console.error('Game heartbeat error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 收藏游戏列表 (含 is_favorited 标记)
 */
export async function getFavorites(): Promise<{
  success: boolean
  data?: ListUserGamesData
  error?: string
}> {
  try {
    const response = await post<object, ListUserGamesData>('/api/v1/game/favorites', {}, { auth: true })
    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }
    return { success: false, error: response.msg || '获取收藏列表失败' }
  } catch (error) {
    console.error('Get favorites error:', error)
    return { success: false, error: error instanceof Error ? error.message : '网络错误' }
  }
}

/**
 * 添加收藏
 */
export async function addFavorite(gameCode: string, providerCode: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<{ game_code: string; provider_code: string }, object>(
      '/api/v1/game/favorite/add',
      { game_code: gameCode, provider_code: providerCode },
      { auth: true }
    )
    if (response.code === 0) return { success: true }
    return { success: false, error: response.msg || '收藏失败' }
  } catch (error) {
    console.error('Add favorite error:', error)
    return { success: false, error: error instanceof Error ? error.message : '网络错误' }
  }
}

/**
 * 取消收藏
 */
export async function removeFavorite(gameCode: string, providerCode: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<{ game_code: string; provider_code: string }, object>(
      '/api/v1/game/favorite/remove',
      { game_code: gameCode, provider_code: providerCode },
      { auth: true }
    )
    if (response.code === 0) return { success: true }
    return { success: false, error: response.msg || '取消收藏失败' }
  } catch (error) {
    console.error('Remove favorite error:', error)
    return { success: false, error: error instanceof Error ? error.message : '网络错误' }
  }
}

/**
 * 获取投注记录列表 (最远可查上个月 1 号)
 */
export async function getBetRecords(params: Omit<ListBetRecordsRequest, keyof SecurityFields> = {}): Promise<{
  success: boolean
  data?: ListBetRecordsData
  error?: string
}> {
  try {
    const response = await post<typeof params, ListBetRecordsData>('/api/v1/game/bet-records', params, { auth: true })

    if (response.code === 0) {
      return { success: true, data: response.data ?? { items: [], pagination: {} } }
    }
    return { success: false, error: response.msg || '获取投注记录失败' }
  } catch (error) {
    console.error('Get bet records error:', error)
    return { success: false, error: error instanceof Error ? error.message : '网络错误' }
  }
}
